/**
 * The design's recurring objects, once each.
 *
 * A course row, a course tile, a deadline pill, a visibility control, a
 * progress line, a ⋯ menu. Every one of them appears on three or more screens
 * in the design (1a, 1d, 1g and the catalogue all draw the same course), and
 * the pill in particular carries a rule that must not be restated: the stored
 * visibility `assigned` is shown as **Invite only**, everywhere, because
 * "assigned" reads as a state and the column beside it is the state.
 */

import { useEffect, useState } from 'react';
import type React from 'react';
import type {
  AssignedCourseWire,
  BundleCardWire,
  CourseCardWire,
  CourseState,
  DeadlineBadge,
  Visibility
} from '@wire';
import { ds } from '../ds';
import { Link, useRouter } from '../router';
import { HAIR, RADIUS, deadlineLabel, display, initials, meta, minutesLabel, monoPlain, plural, safeColour } from '../theme';

/** The pill's words. `assigned` is "Invite only" — see the file header. */
export const VISIBILITY_LABEL: Record<Visibility, string> = {
  private: 'Private',
  assigned: 'Invite only',
  unlisted: 'Unlisted',
  listed: 'Listed'
};

export const STATE_LABEL: Record<CourseState, string> = {
  draft: 'Draft',
  live: 'Live',
  archived: 'Archived'
};

/** The deadline badge, in all three of its states. */
export function DeadlinePill({
  badge,
  dueAt,
  daysLate
}: {
  badge: DeadlineBadge;
  dueAt: number | null;
  daysLate: number;
}) {
  const { Badge } = ds();
  return (
    <Badge tone={badge === 'overdue' ? 'danger' : badge === 'due' ? 'warning' : 'neutral'} mono>
      {deadlineLabel(badge, dueAt, daysLate)}
    </Badge>
  );
}

/** Draft / Live / Archived. Live is the ordinary state, so it is the quiet one
 *  — a badge on every row is a badge nobody reads. */
export function StateBadge({ state }: { state: CourseState }) {
  const { Badge } = ds();
  if (state === 'live') return <Badge tone="success" dot>Live</Badge>;
  return <Badge tone={state === 'draft' ? 'signal' : 'neutral'}>{STATE_LABEL[state]}</Badge>;
}

/**
 * The visibility pill, as a control (screen 1d).
 *
 * A native `<select>` wearing the pill: the choice is four values in a table
 * row, and a popover would need its own keyboard handling for something the
 * browser already does. The label is the only interesting part — see the file
 * header.
 */
export function VisibilitySelect({
  value,
  disabled,
  onChange
}: {
  value: Visibility;
  disabled?: boolean;
  onChange: (next: Visibility) => void;
}) {
  return (
    <select
      className="mv-pill-select"
      aria-label="Who can find this course"
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value as Visibility)}
    >
      {(Object.keys(VISIBILITY_LABEL) as Visibility[]).map((v) => (
        <option key={v} value={v}>
          {VISIBILITY_LABEL[v]}
        </option>
      ))}
    </select>
  );
}

/** The course's colour as a tile, with a book in it. The colour is derived from
 *  the id server-side, so the same course is the same colour everywhere. */
export function CourseMark({ colour, size = 40 }: { colour: string; size?: number }) {
  const { Icon } = ds();
  return (
    <span
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        flex: 'none',
        borderRadius: size >= 40 ? 12 : 9,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: safeColour(colour),
        color: '#fff'
      }}
    >
      <Icon name="BookOpen" size={Math.round(size * 0.44)} color="currentColor" />
    </span>
  );
}

/**
 * A bar and its number, the design's own arrangement — percentage at the right
 * end, in mono.
 *
 * No `width` means the full column. Deliberately not a default of 160: a
 * default would be chosen by passing `undefined`, which is exactly what a
 * caller writes when it means "as wide as you like".
 */
export function ProgressLine({ percent, width }: { percent: number; width?: number }) {
  const { Progress } = ds();
  return (
    <Progress
      value={percent}
      showValue
      size="sm"
      style={width === undefined ? { flex: 1, minWidth: 0 } : { width, flex: 'none' }}
    />
  );
}

/** Initials in a circle — "Also on this bundle" (1g) and the assign dialog's
 *  suggestions. The design never shows a photo in either place. */
export function Avatar({ name, handle }: { name: string; handle?: string | null }) {
  return (
    <span
      title={name}
      style={{
        width: 28,
        height: 28,
        borderRadius: 999,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--primary-soft)',
        color: 'var(--primary-text)',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '.02em'
      }}
    >
      {initials(name, handle ?? name)}
    </span>
  );
}

/** "8 lessons · 32 min · lesson 3 of 8" — the mono line under a course title. */
export function courseMeta(item: AssignedCourseWire): string {
  const { course, enrolment } = item;
  const lessons = course.lessonCount === null ? null : plural(course.lessonCount, 'lesson');
  const where =
    enrolment && enrolment.lessonIndex !== null && course.lessonCount
      ? `lesson ${enrolment.lessonIndex + 1} of ${course.lessonCount}`
      : null;
  return meta(lessons, minutesLabel(course.minutes), where);
}

/**
 * One row of "Assigned to you" (1a) and of My learning.
 *
 * The button goes straight into the course rather than to its page: somebody
 * with three days left and a half-finished lesson wants the lesson, and the
 * course page is one more click in front of it. The title still links there.
 */
export function AssignedRow({ item }: { item: AssignedCourseWire }) {
  const { Button } = ds();
  const { navigate } = useRouter();
  const { course, enrolment } = item;
  const started = !!enrolment && enrolment.percent > 0;
  const finished = !!enrolment?.completedAt;

  return (
    <div
      className="mv-row"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '14px 4px',
        borderTop: HAIR
      }}
    >
      <CourseMark colour={course.colour} />

      <div style={{ flex: 1, minWidth: 0, display: 'grid', gap: 3 }}>
        <Link to={`/course/${course.id}`}>
          <span
            style={{
              fontSize: 14.5,
              fontWeight: 600,
              color: 'var(--text-strong)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block'
            }}
          >
            {course.title}
          </span>
        </Link>
        <span style={monoPlain}>{courseMeta(item)}</span>
      </div>

      <ProgressLine percent={enrolment?.percent ?? 0} width={140} />
      <DeadlinePill badge={item.badge} dueAt={item.dueAt} daysLate={item.daysLate} />

      <Button
        variant={finished ? 'secondary' : 'primary'}
        size="sm"
        onClick={() => navigate(`/course/${course.id}/play`)}
      >
        {finished ? 'Open again' : started ? 'Continue' : 'Start'}
      </Button>
    </div>
  );
}

/** A course as a card — the catalogue, and "New in your company" (1a). */
export function CourseTile({ course }: { course: CourseCardWire }) {
  const { Card } = ds();
  const { navigate } = useRouter();

  return (
    <Card
      padding="md"
      interactive
      radius={RADIUS.card}
      onClick={() => navigate(`/course/${course.id}`)}
      style={{ display: 'grid', gap: 10, alignContent: 'start' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <CourseMark colour={course.colour} size={32} />
        {course.code && <span style={monoPlain}>{course.code}</span>}
      </div>
      {/* The card's own onClick is a convenience; the title is the real
          destination, so it is a Link a keyboard or screen reader can reach —
          the card has no role or key handler of its own. */}
      <h3 style={{ ...display(16), lineHeight: 1.2 }} onClick={(e) => e.stopPropagation()}>
        <Link to={`/course/${course.id}`}>{course.title}</Link>
      </h3>
      <span style={monoPlain}>
        {meta(
          course.lessonCount === null ? null : plural(course.lessonCount, 'lesson'),
          minutesLabel(course.minutes)
        )}
      </span>
    </Card>
  );
}

/** A bundle as a card — "Bundles for <team>" (1a) and the catalogue. */
export function BundleTile({ bundle }: { bundle: BundleCardWire }) {
  const { Card, Badge } = ds();
  const { navigate } = useRouter();

  return (
    <Card
      padding="md"
      interactive
      radius={RADIUS.card}
      onClick={() => navigate(`/bundle/${bundle.id}`)}
      style={{ display: 'grid', gap: 10, alignContent: 'start' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span
          aria-hidden="true"
          style={{ width: 26, height: 26, borderRadius: 8, background: safeColour(bundle.colour) }}
        />
        <Badge mono>{bundle.ordered ? 'Bundle · in order' : 'Bundle'}</Badge>
      </div>
      {/* Same reasoning as CourseTile above: the title is the destination, not
          the card's onClick. */}
      <h3 style={{ ...display(16), lineHeight: 1.2 }} onClick={(e) => e.stopPropagation()}>
        <Link to={`/bundle/${bundle.id}`}>{bundle.title}</Link>
      </h3>
      <span style={monoPlain}>
        {meta(
          plural(bundle.courseCount, 'course'),
          bundle.completedCount > 0 ? `${bundle.completedCount} done` : null
        )}
      </span>
      <ProgressLine percent={bundle.percent} />
    </Card>
  );
}

export interface MenuItem {
  label: string;
  onSelect: () => void;
  /** Destructive, and rendered as such. */
  danger?: boolean;
  disabled?: boolean;
}

/**
 * The ⋯ menu on a course row (1d).
 *
 * Closes on Escape and on a click anywhere else — the scrim is a real button so
 * that click cannot also land on the row underneath, which on this page would
 * be a second menu opening as the first one shut.
 */
export function Menu({ label, items }: { label: string; items: MenuItem[] }) {
  const { IconButton } = ds();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      <IconButton icon="MoreHorizontal" label={label} onClick={() => setOpen((v) => !v)} />
      {open && (
        <>
          <button
            type="button"
            className="mv-scrim"
            aria-label="Close the menu"
            onClick={() => setOpen(false)}
          />
          <div
            role="menu"
            style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: 6,
              zIndex: 41,
              minWidth: 168,
              padding: 6,
              display: 'grid',
              gap: 2,
              background: 'var(--surface-card)',
              border: HAIR,
              borderRadius: RADIUS.control,
              boxShadow: '0 18px 44px rgba(40,28,45,.18)'
            }}
          >
            {items.map((item) => (
              <button
                key={item.label}
                type="button"
                role="menuitem"
                disabled={item.disabled}
                onClick={() => {
                  setOpen(false);
                  item.onSelect();
                }}
                style={{
                  textAlign: 'left',
                  padding: '8px 10px',
                  border: 'none',
                  borderRadius: 8,
                  background: 'transparent',
                  fontSize: 13.5,
                  color: item.danger ? 'var(--danger-600)' : 'var(--text-body)'
                }}
              >
                {item.label}
              </button>
            ))}
          </div>
        </>
      )}
    </span>
  );
}

/** A row of the design's stat tiles, on the card the design puts them on. */
export function StatRow({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gap: 16,
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))'
      }}
    >
      {children}
    </div>
  );
}

/** The shelf grid — cards that wrap without a breakpoint each. */
export function TileGrid({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gap: 16,
        gridTemplateColumns: 'repeat(auto-fill, minmax(232px, 1fr))'
      }}
    >
      {children}
    </div>
  );
}
